import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Italic,
  Link2,
  List,
  ListOrdered,
  Redo2,
  Underline,
  Undo2,
} from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { promptModal } from '@/components/ui/modal'
import { Button } from '@/components/ui/button'
import { Select } from '@/components/ui/select'
import { DOC_FONTS, type SablonSurum } from '../types'

const FONT_SIZES = [9, 10, 11, 12, 14, 16, 18, 20, 24]

type Props = {
  surum: SablonSurum
  onMetaChange: (patch: Partial<Pick<SablonSurum, 'editorFont' | 'editorFontSize' | 'lineHeight'>>) => void
  onCommand?: (command: string, value?: string) => void
}

function runCommand(command: string, value?: string) {
  document.execCommand(command, false, value)
}

export function DocumentToolbar({ surum, onMetaChange, onCommand }: Props) {
  const { t } = useTranslation()

  function tool(command: string, value?: string) {
    runCommand(command, value)
    onCommand?.(command, value)
  }

  function insertLink() {
    void (async () => {
      const url = await promptModal({
        title: t('hr.belgeler.editor.linkPrompt'),
        placeholder: 'https://',
        inputType: 'url',
        confirmLabel: t('common.confirm'),
      })
      if (!url?.trim()) return
      tool('createLink', url.trim())
    })()
  }

  return (
    <div
      className="flex flex-wrap items-center gap-0.5 border-b border-border/70 bg-muted/30 px-1.5 py-1"
      role="toolbar"
      aria-label={t('hr.belgeler.editor.toolbar')}
    >
      <Select
        size="xs"
        preserveFocus
        className="w-auto max-w-[8.5rem]"
        value={surum.editorFont}
        onChange={(v) => {
          onMetaChange({ editorFont: v })
          tool('fontName', v.split(',')[0]?.replace(/"/g, '') ?? 'Arial')
        }}
        options={DOC_FONTS.map((f) => ({ value: f.value, label: f.label }))}
        aria-label={t('hr.belgeler.editor.font')}
      />
      <Select
        size="xs"
        preserveFocus
        className="w-14"
        value={String(surum.editorFontSize)}
        onChange={(v) => onMetaChange({ editorFontSize: Number(v) })}
        options={FONT_SIZES.map((n) => ({ value: String(n), label: String(n) }))}
        aria-label={t('hr.belgeler.editor.fontSize')}
      />
      <span className="mx-0.5 h-4 w-px bg-border" aria-hidden />
      <ToolBtn label={t('hr.belgeler.editor.bold')} onClick={() => tool('bold')}>
        <Bold />
      </ToolBtn>
      <ToolBtn label={t('hr.belgeler.editor.italic')} onClick={() => tool('italic')}>
        <Italic />
      </ToolBtn>
      <ToolBtn label={t('hr.belgeler.editor.underline')} onClick={() => tool('underline')}>
        <Underline />
      </ToolBtn>
      <span className="mx-0.5 h-4 w-px bg-border" aria-hidden />
      <ToolBtn label={t('hr.belgeler.editor.alignLeft')} onClick={() => tool('justifyLeft')}>
        <AlignLeft />
      </ToolBtn>
      <ToolBtn label={t('hr.belgeler.editor.alignCenter')} onClick={() => tool('justifyCenter')}>
        <AlignCenter />
      </ToolBtn>
      <ToolBtn label={t('hr.belgeler.editor.alignRight')} onClick={() => tool('justifyRight')}>
        <AlignRight />
      </ToolBtn>
      <span className="mx-0.5 h-4 w-px bg-border" aria-hidden />
      <ToolBtn label={t('hr.belgeler.editor.bulletList')} onClick={() => tool('insertUnorderedList')}>
        <List />
      </ToolBtn>
      <ToolBtn label={t('hr.belgeler.editor.numberedList')} onClick={() => tool('insertOrderedList')}>
        <ListOrdered />
      </ToolBtn>
      <ToolBtn label={t('hr.belgeler.editor.link')} onClick={insertLink}>
        <Link2 />
      </ToolBtn>
      <span className="mx-0.5 h-4 w-px bg-border" aria-hidden />
      <ToolBtn label={t('hr.belgeler.editor.undo')} onClick={() => tool('undo')}>
        <Undo2 />
      </ToolBtn>
      <ToolBtn label={t('hr.belgeler.editor.redo')} onClick={() => tool('redo')}>
        <Redo2 />
      </ToolBtn>
    </div>
  )
}

function ToolBtn({ label, onClick, children }: { label: string; onClick: () => void; children: ReactNode }) {
  return (
    <Button type="button" variant="ghost" size="icon" className="size-7" aria-label={label} title={label} onClick={onClick}>
      {children}
    </Button>
  )
}
